import { Add, People, SearchOff } from "@mui/icons-material"
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Employee, useGetEmployeesQuery } from "../../apis/appApi"
import { EmployeeDetailDrawer } from "./EmployeeDetailDrawer"
import { EmployeeFormDialog } from "./EmployeeFormDialog"
import { EmployeesGrid } from "./EmployeesGrid"

export const EmployeesPage: FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [showDisabled, setShowDisabled] = useState(false)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(
    null,
  )
  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null)

  const { data: employees, isLoading } = useGetEmployeesQuery({
    withDisabled: showDisabled,
    search: search || undefined,
  })

  const selectedEmployee = useMemo(
    () => employees?.find((e) => e.id === selectedEmployeeId) ?? null,
    [employees, selectedEmployeeId],
  )

  const handleRowClick = useCallback((id: number) => {
    setSelectedEmployeeId(id)
  }, [])

  const handleDrawerClose = useCallback(() => {
    setSelectedEmployeeId(null)
  }, [])

  const handleEdit = useCallback((employee: Employee) => {
    setEditingEmployee(employee)
    setFormDialogOpen(true)
  }, [])

  const handleCreate = useCallback(() => {
    setEditingEmployee(null)
    setFormDialogOpen(true)
  }, [])

  const handleFormClose = useCallback(() => {
    setFormDialogOpen(false)
    setEditingEmployee(null)
  }, [])

  const rows = employees ?? []
  const hasNoData = rows.length === 0 && !search

  return (
    <Box
      data-testid="employees-page"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? 1.5 : 0,
        }}
      >
        {/* Row 1: title + create button */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t("employees.title")}
          </Typography>
          {isMobile ? (
            <Button
              data-testid="employees-create-btn"
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={handleCreate}
            >
              {t("employees.create")}
            </Button>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                data-testid="employees-search-input"
                size="small"
                placeholder={t("employees.search")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setSearch(searchInput.trim())
                }}
                sx={{ width: 260 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    data-testid="employees-disabled-switch"
                    size="small"
                    checked={showDisabled}
                    onChange={(_, v) => setShowDisabled(v)}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t("employees.showDisabled")}
                  </Typography>
                }
              />
              <Button
                data-testid="employees-create-btn"
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={handleCreate}
              >
                {t("employees.create")}
              </Button>
            </Stack>
          )}
        </Stack>

        {/* Row 2 (mobile only): search + switch */}
        {isMobile && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              data-testid="employees-search-input"
              size="small"
              placeholder={t("employees.search")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setSearch(searchInput.trim())
              }}
              sx={{ flex: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  data-testid="employees-disabled-switch"
                  size="small"
                  checked={showDisabled}
                  onChange={(_, v) => setShowDisabled(v)}
                />
              }
              label={
                <Typography variant="body2" noWrap>
                  {t("employees.showDisabled")}
                </Typography>
              }
            />
          </Stack>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {isLoading ? (
          <Box
            data-testid="employees-loading"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            {hasNoData ? (
              <Stack
                data-testid="employees-empty-state"
                alignItems="center"
                spacing={2}
                sx={{ maxWidth: 360 }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <People sx={{ fontSize: 36, color: "grey.400" }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  {t("employees.emptyDay.title")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  {t("employees.emptyDay.description")}
                </Typography>
                <Button
                  data-testid="employees-empty-create-btn"
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleCreate}
                >
                  {t("employees.create")}
                </Button>
              </Stack>
            ) : (
              <Stack
                data-testid="employees-empty-filter"
                alignItems="center"
                spacing={1.5}
                sx={{ maxWidth: 320 }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SearchOff sx={{ fontSize: 28, color: "grey.400" }} />
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="text.primary"
                >
                  {t("employees.emptyFilter.title")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  {t("employees.emptyFilter.description")}
                </Typography>
              </Stack>
            )}
          </Box>
        ) : (
          <EmployeesGrid
            rows={rows}
            loading={isLoading}
            onRowClick={handleRowClick}
          />
        )}
      </Box>

      {/* Detail drawer */}
      <EmployeeDetailDrawer
        employee={selectedEmployee}
        open={!!selectedEmployeeId}
        onClose={handleDrawerClose}
        onEdit={handleEdit}
      />

      {/* Create/Edit dialog */}
      <EmployeeFormDialog
        open={formDialogOpen}
        onClose={handleFormClose}
        employee={editingEmployee}
      />
    </Box>
  )
}
